import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Boxes,
  Truck,
  Building2,
  Package,
  CheckCircle2,
  Sparkles,
  CreditCard,
  Loader2,
  ArrowRight,
  Shield,
  Zap,
  Clock,
  Star,
  CheckCircle,
  XCircle,
  UsersRound,
  Info
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Carica Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

// Componente form carta per trial
const TrialCardForm = ({ 
  clientSecret, 
  packageInfo, 
  onSuccess, 
  onCancel 
}: { 
  clientSecret: string; 
  packageInfo: any;
  onSuccess: () => void; 
  onCancel: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Errore nel caricamento del form carta');
      setProcessing(false);
      return;
    }

    // Conferma il SetupIntent
    const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    if (stripeError) {
      setError(stripeError.message || 'Errore nella verifica della carta');
      setProcessing(false);
      return;
    }

    if (setupIntent?.status === 'succeeded') {
      // Conferma il trial sul backend
      try {
        await axios.post(`${API}/payments/trial/confirm?setup_intent_id=${setupIntent.id}`);
        onSuccess();
      } catch (err) {
        setError(getErrorMessage(err, 'Errore nell\'attivazione del trial'));
      }
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-muted rounded-lg space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <Shield className="h-4 w-4 text-green-500" />
          <span className="font-medium">Pagamento sicuro con Stripe</span>
        </div>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': { color: '#aab7c4' },
              },
              invalid: { color: '#9e2146' },
            },
          }}
          className="p-3 border rounded-md bg-white"
        />
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm flex items-center gap-2">
          <XCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="p-3 bg-blue-50 rounded-md text-sm text-blue-800 flex items-start gap-2">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">La carta non verrà addebitata ora</p>
          <p className="text-blue-600">
            Dopo {packageInfo?.trial_days} giorni, se non annulli, verrà addebitato {formatCurrency(packageInfo?.price_after_trial)}/{packageInfo?.billing_period === 'yearly' ? 'anno' : 'mese'}
          </p>
        </div>
      </div>

      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onCancel} disabled={processing} className="flex-1">
          Annulla
        </Button>
        <Button type="submit" disabled={!stripe || processing} className="flex-1">
          {processing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Verifica...
            </>
          ) : (
            <>
              <Shield className="h-4 w-4 mr-2" />
              Attiva Prova Gratuita
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

// Icone per estensioni
const EXTENSION_ICONS: Record<string, React.ElementType> = {
  stock: Boxes,
  logistics: Truck,
  multi_site: Building2,
  multi_user: UsersRound,
  bundle_buyer: Package,
  chatbot_ai: Sparkles,
  billing: CreditCard,
  bundle_all: Package
};

// Colori per le card
const EXTENSION_COLORS: Record<string, string> = {
  stock: 'from-blue-500/20 to-blue-600/10',
  logistics: 'from-green-500/20 to-green-600/10',
  multi_site: 'from-purple-500/20 to-purple-600/10',
  multi_user: 'from-cyan-500/20 to-cyan-600/10',
  bundle_buyer: 'from-amber-500/20 to-amber-600/10',
  chatbot_ai: 'from-pink-500/20 to-pink-600/10',
  billing: 'from-emerald-500/20 to-emerald-600/10',
  bundle_all: 'from-amber-500/20 to-amber-600/10'
};

// Estensioni per ruolo
const SELLER_EXTENSIONS = ['stock', 'logistics', 'chatbot_ai', 'billing', 'bundle_all'];
const BUYER_EXTENSIONS = ['multi_site', 'multi_user', 'bundle_buyer'];

interface ExtensionPackage {
  code: string;
  name: string;
  description: string;
  monthly_price: number;
  yearly_price: number;
  yearly_savings: number;
  features: string[];
  trial_days: number;
}

interface Transaction {
  session_id?: string;
  order_id?: string;
  extension_code: string;
  billing_period: string;
  amount: number;
  currency: string;
  payment_status: string;
  created_at: string;
  provider?: string;
}

interface PaymentConfig {
  providers: {
    stripe: { enabled: boolean; name: string };
    paypal: { enabled: boolean; name: string; client_id: string | null };
  };
}

const ExtensionStorePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isBuyer } = useAuth();
  
  const [packages, setPackages] = useState<ExtensionPackage[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');
  const [processingPackage, setProcessingPackage] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  
  // Payment method selection
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [paypalOrderId, setPaypalOrderId] = useState<string | null>(null);
  
  // Trial status
  const [trialStatus, setTrialStatus] = useState<Record<string, any>>({});
  const [startingTrial, setStartingTrial] = useState<string | null>(null);
  
  // Trial con carta
  const [showTrialCardDialog, setShowTrialCardDialog] = useState(false);
  const [trialClientSecret, setTrialClientSecret] = useState<string | null>(null);
  const [trialPackageInfo, setTrialPackageInfo] = useState<any>(null);

  // Filtra i pacchetti in base al ruolo dell'utente
  const filteredPackages = packages.filter(pkg => {
    if (isBuyer) {
      // Buyer vede solo multi_site
      return BUYER_EXTENSIONS.includes(pkg.code);
    } else {
      // Seller vede stock, logistics e bundle
      return SELLER_EXTENSIONS.includes(pkg.code);
    }
  });

  // Check URL params per gestire redirect da Stripe/PayPal
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const provider = searchParams.get('provider');
    const orderId = searchParams.get('order_id');

    if (paymentStatus === 'success') {
      if (provider === 'paypal' && orderId) {
        capturePayPalPayment(orderId);
      } else if (sessionId) {
        verifyStripePayment(sessionId);
      }
    } else if (paymentStatus === 'cancelled') {
      setShowCancelDialog(true);
      navigate('/store', { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [packagesRes, transactionsRes, configRes] = await Promise.all([
        axios.get(`${API}/payments/packages`),
        axios.get(`${API}/payments/transactions`).catch(() => ({ data: { transactions: [] } })),
        axios.get(`${API}/payments/config`).catch(() => ({ data: null }))
      ]);
      
      const pkgs = packagesRes.data.packages || [];
      setPackages(pkgs);
      setTransactions(transactionsRes.data.transactions || []);
      setPaymentConfig(configRes.data);
      
      // Carica lo stato dei trial per i pacchetti che lo supportano
      const trialStatuses: Record<string, any> = {};
      for (const pkg of pkgs) {
        if (pkg.trial_days > 0) {
          try {
            const trialRes = await axios.get(`${API}/payments/trial/status/${pkg.code}`);
            trialStatuses[pkg.code] = trialRes.data;
          } catch {
            trialStatuses[pkg.code] = { has_trial: false, can_start_trial: true };
          }
        }
      }
      setTrialStatus(trialStatuses);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento pacchetti');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async (packageCode: string) => {
    setStartingTrial(packageCode);
    try {
      // Attiva trial gratuito senza carta
      const res = await axios.post(`${API}/payments/trial/start-free/${packageCode}`);
      
      // Mostra successo
      setShowSuccessDialog(true);
      toast.success(`Trial gratuito attivato! Hai ${res.data.days_remaining} giorni di prova.`);
      fetchData();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Errore nell\'attivazione del trial';
      toast.error(message);
    } finally {
      setStartingTrial(null);
    }
  };

  const handleTrialSuccess = () => {
    setShowTrialCardDialog(false);
    setTrialClientSecret(null);
    setTrialPackageInfo(null);
    setShowSuccessDialog(true);
    toast.success('Periodo di prova attivato!');
    fetchData();
  };

  const handleTrialCancel = () => {
    setShowTrialCardDialog(false);
    setTrialClientSecret(null);
    setTrialPackageInfo(null);
  };

  const verifyStripePayment = async (sessionId: string) => {
    setVerifyingPayment(true);
    try {
      const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      
      if (res.data.payment_status === 'paid') {
        setShowSuccessDialog(true);
        toast.success('Pagamento completato! Estensione attivata.');
        fetchData();
      } else if (res.data.status === 'expired') {
        toast.error('La sessione di pagamento è scaduta');
      } else {
        toast.info('Pagamento in elaborazione...');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error(getErrorMessage(error, 'Errore verifica pagamento'));
    } finally {
      setVerifyingPayment(false);
      navigate('/store', { replace: true });
    }
  };

  const capturePayPalPayment = async (orderId: string) => {
    setVerifyingPayment(true);
    try {
      const res = await axios.post(`${API}/payments/paypal/capture/${orderId}`);
      
      if (res.data.payment_status === 'paid') {
        setShowSuccessDialog(true);
        toast.success('Pagamento PayPal completato! Estensione attivata.');
        fetchData();
      } else {
        toast.info('Pagamento in elaborazione...');
      }
    } catch (error) {
      console.error('Error capturing PayPal payment:', error);
      toast.error(getErrorMessage(error, 'Errore cattura pagamento PayPal'));
    } finally {
      setVerifyingPayment(false);
      navigate('/store', { replace: true });
    }
  };

  const handlePurchaseClick = (packageCode: string) => {
    setSelectedPackage(packageCode);
    
    // Se solo Stripe è disponibile, vai direttamente
    if (paymentConfig?.providers.stripe.enabled && !paymentConfig?.providers.paypal.enabled) {
      handleStripePurchase(packageCode);
    } else if (!paymentConfig?.providers.stripe.enabled && paymentConfig?.providers.paypal.enabled) {
      // Solo PayPal disponibile
      setShowPaymentDialog(true);
    } else if (paymentConfig?.providers.stripe.enabled && paymentConfig?.providers.paypal.enabled) {
      // Entrambi disponibili, mostra dialog di scelta
      setShowPaymentDialog(true);
    } else {
      toast.error('Nessun metodo di pagamento configurato');
    }
  };

  const handleStripePurchase = async (packageCode: string) => {
    setProcessingPackage(packageCode);
    setShowPaymentDialog(false);
    
    try {
      const res = await axios.post(`${API}/payments/checkout/create`, {
        extension_code: packageCode,
        billing_period: billingPeriod,
        origin_url: window.location.origin,
        payment_provider: 'stripe'
      });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('URL checkout non ricevuto');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(getErrorMessage(error, 'Errore nella creazione del pagamento'));
      setProcessingPackage(null);
    }
  };

  const createPayPalOrder = useCallback(async () => {
    if (!selectedPackage) return '';
    
    try {
      const res = await axios.post(`${API}/payments/paypal/create-order`, {
        extension_code: selectedPackage,
        billing_period: billingPeriod,
        origin_url: window.location.origin,
        payment_provider: 'paypal'
      });
      
      setPaypalOrderId(res.data.order_id);
      return res.data.order_id;
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      toast.error(getErrorMessage(error, 'Errore creazione ordine PayPal'));
      throw error;
    }
  }, [selectedPackage, billingPeriod]);

  const onPayPalApprove = useCallback(async (data: { orderID: string }) => {
    setShowPaymentDialog(false);
    setVerifyingPayment(true);
    
    try {
      const res = await axios.post(`${API}/payments/paypal/capture/${data.orderID}`);
      
      if (res.data.payment_status === 'paid') {
        setShowSuccessDialog(true);
        toast.success('Pagamento PayPal completato! Estensione attivata.');
        fetchData();
      } else {
        toast.info('Pagamento in elaborazione...');
      }
    } catch (error) {
      console.error('Error capturing PayPal payment:', error);
      toast.error(getErrorMessage(error, 'Errore cattura pagamento'));
    } finally {
      setVerifyingPayment(false);
      setSelectedPackage(null);
    }
  }, []);

  // Verifica se un'estensione è già attiva
  const isExtensionActive = (code: string): boolean => {
    return transactions.some(
      t => t.extension_code === code && t.payment_status === 'paid'
    );
  };

  // Formatta prezzo
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  // Ottieni prezzo del pacchetto selezionato
  const getSelectedPackagePrice = () => {
    if (!selectedPackage) return 0;
    const pkg = packages.find(p => p.code === selectedPackage);
    if (!pkg) return 0;
    return billingPeriod === 'yearly' ? pkg.yearly_price : pkg.monthly_price;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="extension-store-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="extension-store-page">
      {/* Verifica pagamento in corso */}
      {verifyingPayment && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <Card className="w-96">
            <CardContent className="flex flex-col items-center justify-center py-8 gap-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-lg font-medium">Verifica pagamento in corso...</p>
              <p className="text-sm text-muted-foreground">Attendere prego</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
          <Sparkles className="h-4 w-4" />
          Potenzia la tua piattaforma
        </div>
        <h1 className="text-4xl font-heading font-bold">
          Estensioni Premium
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Sblocca funzionalità avanzate per ottimizzare la gestione del tuo business HORECA
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
          Mensile
        </span>
        <Switch
          checked={billingPeriod === 'yearly'}
          onCheckedChange={(checked) => setBillingPeriod(checked ? 'yearly' : 'monthly')}
          data-testid="billing-toggle"
        />
        <span className={`text-sm font-medium ${billingPeriod === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}>
          Annuale
        </span>
        {billingPeriod === 'yearly' && (
          <Badge variant="secondary" className="bg-green-500/10 text-green-600 border-green-500/30">
            Risparmia 20%
          </Badge>
        )}
      </div>

      {/* Packages Grid */}
      <div className={`grid gap-6 items-stretch ${
        filteredPackages.length === 1 
          ? 'grid-cols-1 max-w-md mx-auto' 
          : filteredPackages.length <= 2 
            ? 'grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto'
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      }`}>
        {filteredPackages.map((pkg) => {
          const Icon = EXTENSION_ICONS[pkg.code] || Package;
          const isActive = isExtensionActive(pkg.code);
          const price = billingPeriod === 'yearly' ? pkg.yearly_price : pkg.monthly_price;
          const monthlyEquivalent = billingPeriod === 'yearly' ? pkg.yearly_price / 12 : pkg.monthly_price;
          const hasTrial = pkg.trial_days > 0;
          const pkgTrialStatus = trialStatus[pkg.code];
          const isTrialActive = pkgTrialStatus?.status === 'active';
          const trialExpired = pkgTrialStatus?.status === 'expired';
          const canStartTrial = pkgTrialStatus?.can_start_trial && !isActive;
          const isBundle = pkg.code === 'bundle_all' || pkg.code === 'bundle_buyer';
          
          return (
            <Card 
              key={pkg.code} 
              className={`relative overflow-hidden transition-all hover:shadow-lg flex flex-col ${isBundle ? 'border-primary ring-2 ring-primary/20' : ''} ${isActive ? 'opacity-75' : ''}`}
              data-testid={`package-card-${pkg.code}`}
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${EXTENSION_COLORS[pkg.code]} opacity-50`} />
              
              {/* Trial Badge */}
              {hasTrial && canStartTrial && !isBundle && (
                <div className="absolute top-3 right-3">
                  <Badge className="bg-pink-500 text-white">
                    <Clock className="h-3 w-3 mr-1" />
                    {pkg.trial_days}gg Gratis
                  </Badge>
                </div>
              )}
              
              {/* Bundle Badge */}
              {isBundle && (
                <div className="absolute top-3 right-3">
                  <Badge className="bg-primary text-primary-foreground">
                    <Star className="h-3 w-3 mr-1" />
                    Consigliato
                  </Badge>
                </div>
              )}

              {/* Active Badge */}
              {isActive && (
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary" className="bg-green-500 text-white">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Attivo
                  </Badge>
                </div>
              )}
              
              {/* Trial Active Badge */}
              {isTrialActive && !isActive && (
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary" className="bg-pink-500 text-white">
                    <Clock className="h-3 w-3 mr-1" />
                    Trial: {pkgTrialStatus.days_remaining}gg rimasti
                  </Badge>
                </div>
              )}
              
              {/* Trial Expired Badge */}
              {trialExpired && !isActive && (
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary" className="bg-gray-500 text-white">
                    <XCircle className="h-3 w-3 mr-1" />
                    Trial Scaduto
                  </Badge>
                </div>
              )}

              <CardHeader className="relative">
                <div className={`p-3 rounded-xl w-fit ${isBundle ? 'bg-primary/20' : 'bg-muted'}`}>
                  <Icon className={`h-8 w-8 ${isBundle ? 'text-primary' : 'text-foreground'}`} />
                </div>
                <CardTitle className="text-xl mt-4">{pkg.name}</CardTitle>
                <CardDescription>{pkg.description}</CardDescription>
              </CardHeader>

              <CardContent className="relative space-y-4 flex-1 flex flex-col">
                {/* Price */}
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold">{formatPrice(monthlyEquivalent)}</span>
                    <span className="text-muted-foreground">/mese</span>
                  </div>
                  {billingPeriod === 'yearly' && (
                    <p className="text-sm text-muted-foreground">
                      {formatPrice(price)} fatturati annualmente
                    </p>
                  )}
                  {billingPeriod === 'yearly' && pkg.yearly_savings > 0 && (
                    <p className="text-sm text-green-600 font-medium">
                      Risparmi {formatPrice(pkg.yearly_savings)}/anno
                    </p>
                  )}
                </div>

                <Separator />

                {/* Features */}
                <ul className="space-y-2 flex-1">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="relative flex-col gap-2 pt-2">
                {/* Trial Button - solo se disponibile e non già attivo */}
                {hasTrial && canStartTrial && !isTrialActive && (
                  <Button 
                    className="w-full bg-pink-500 hover:bg-pink-600 text-white text-sm truncate" 
                    size="default"
                    disabled={startingTrial === pkg.code}
                    onClick={() => handleStartTrial(pkg.code)}
                    data-testid={`trial-btn-${pkg.code}`}
                  >
                    {startingTrial === pkg.code ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin shrink-0" />
                        Attivazione...
                      </>
                    ) : (
                      <>
                        <Clock className="h-4 w-4 mr-1.5 shrink-0" />
                        Prova Gratis {pkg.trial_days}gg
                      </>
                    )}
                  </Button>
                )}
                
                {/* Purchase Button */}
                <Button 
                  className="w-full text-sm" 
                  variant={isBundle ? 'default' : (hasTrial && canStartTrial ? 'outline' : 'default')}
                  size="default"
                  disabled={isActive || processingPackage === pkg.code || isTrialActive}
                  onClick={() => handlePurchaseClick(pkg.code)}
                  data-testid={`purchase-btn-${pkg.code}`}
                >
                  {processingPackage === pkg.code ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin shrink-0" />
                      Elaborazione...
                    </>
                  ) : isActive ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1.5 shrink-0" />
                      Già Attivo
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-1.5 shrink-0" />
                      Acquista Ora
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Trust Badges */}
      <div className="flex flex-wrap items-center justify-center gap-8 py-8">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Shield className="h-5 w-5" />
          <span className="text-sm">Pagamenti sicuri</span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Zap className="h-5 w-5" />
          <span className="text-sm">Attivazione immediata</span>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="h-5 w-5" />
          <span className="text-sm">Cancella quando vuoi</span>
        </div>
      </div>

      {/* Transaction History */}
      {transactions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Storico Acquisti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {transactions.slice(0, 5).map((tx, idx) => (
                <div 
                  key={tx.session_id || tx.order_id || idx} 
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    {tx.payment_status === 'paid' ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : tx.payment_status === 'pending' ? (
                      <Clock className="h-5 w-5 text-amber-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    <div>
                      <p className="font-medium">
                        {packages.find(p => p.code === tx.extension_code)?.name || tx.extension_code}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(tx.created_at).toLocaleDateString('it-IT')} • {tx.billing_period === 'yearly' ? 'Annuale' : 'Mensile'}
                        {tx.provider && <span className="ml-1">• {tx.provider === 'paypal' ? 'PayPal' : 'Carta'}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatPrice(tx.amount)}</p>
                    <Badge variant="outline" className={
                      tx.payment_status === 'paid' ? 'text-green-600' : 
                      tx.payment_status === 'pending' ? 'text-amber-600' : 'text-red-600'
                    }>
                      {tx.payment_status === 'paid' ? 'Completato' : 
                       tx.payment_status === 'pending' ? 'In attesa' : 'Fallito'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Method Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Scegli metodo di pagamento</DialogTitle>
            <DialogDescription>
              {selectedPackage && packages.find(p => p.code === selectedPackage)?.name} - {formatPrice(getSelectedPackagePrice())}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Stripe Option */}
            {paymentConfig?.providers.stripe.enabled && (
              <Button
                variant="outline"
                className="w-full h-14 justify-start gap-3"
                onClick={() => selectedPackage && handleStripePurchase(selectedPackage)}
                data-testid="pay-with-stripe"
              >
                <CreditCard className="h-6 w-6" />
                <div className="text-left">
                  <p className="font-medium">Carta di Credito</p>
                  <p className="text-xs text-muted-foreground">Visa, Mastercard, Amex</p>
                </div>
              </Button>
            )}
            
            {/* PayPal Option */}
            {paymentConfig?.providers.paypal.enabled && paymentConfig.providers.paypal.client_id && (
              <div className="w-full" data-testid="pay-with-paypal">
                <PayPalScriptProvider 
                  options={{ 
                    clientId: paymentConfig.providers.paypal.client_id,
                    currency: "EUR",
                    intent: "capture"
                  }}
                >
                  <PayPalButtons
                    style={{ 
                      layout: "horizontal",
                      color: "blue",
                      shape: "rect",
                      label: "paypal",
                      height: 48
                    }}
                    createOrder={createPayPalOrder}
                    onApprove={onPayPalApprove}
                    onError={(err) => {
                      console.error('PayPal error:', err);
                      toast.error('Errore PayPal');
                    }}
                    onCancel={() => {
                      toast.info('Pagamento annullato');
                    }}
                  />
                </PayPalScriptProvider>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPaymentDialog(false)}>
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent>
          <DialogHeader>
            <div className="mx-auto mb-4 p-3 rounded-full bg-green-500/10 w-fit">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <DialogTitle className="text-center text-2xl">Pagamento Completato!</DialogTitle>
            <DialogDescription className="text-center">
              La tua estensione è stata attivata con successo. Puoi iniziare a usarla subito.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center gap-2">
            <Button onClick={() => setShowSuccessDialog(false)}>
              Continua
            </Button>
            <Button variant="outline" onClick={() => navigate('/settings')}>
              Vai alle Impostazioni
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <div className="mx-auto mb-4 p-3 rounded-full bg-amber-500/10 w-fit">
              <XCircle className="h-10 w-10 text-amber-500" />
            </div>
            <DialogTitle className="text-center text-2xl">Pagamento Annullato</DialogTitle>
            <DialogDescription className="text-center">
              Il pagamento è stato annullato. Puoi riprovare quando vuoi.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button onClick={() => setShowCancelDialog(false)}>
              Ho capito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trial Card Dialog */}
      <Dialog open={showTrialCardDialog} onOpenChange={setShowTrialCardDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Attiva Prova Gratuita
            </DialogTitle>
            <DialogDescription>
              Inserisci una carta come garanzia per attivare {trialPackageInfo?.trial_days} giorni di prova gratuita.
              Non ti verrà addebitato nulla oggi.
            </DialogDescription>
          </DialogHeader>
          
          {trialClientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret: trialClientSecret }}>
              <TrialCardForm
                clientSecret={trialClientSecret}
                packageInfo={trialPackageInfo}
                onSuccess={handleTrialSuccess}
                onCancel={handleTrialCancel}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExtensionStorePage;
